import { useMemo, useState } from "react";
import { useMutation, useQuery } from "convex/react";
import { PageHeader } from "../PageHeader";
import { QueueTable } from "../shared/QueueTable";
import type { Severity } from "../shared/EventRow";
import { isoFrom } from "../auditNormalize";
import { api as convexApi } from "../../../lib/convexApi";

// The anomaly scan cron has run every 15 minutes since it shipped, writing
// findings to the `anomalies` table that nothing could read: activeForOrg and
// dismiss had no caller anywhere in the repo. A detector whose findings are
// unreachable detects nothing, so this page is the read side of that cron.

interface Finding {
  id: string;
  detector: string;
  severity: string;
  subjectKind: string;
  subject: string;
  count: number;
  threshold: number;
  windowStart: number;
  windowEnd: number;
  status: string;
  createdAt: number;
  updatedAt: number;
}

// Detector ids come from shared-lib/src/anomalyDetect.ts. An unknown id falls
// back to the raw string rather than being dropped — a detector added there and
// not here must still be visible, just less prettily.
const DETECTOR_LABELS: Record<string, string> = {
  "mcp-call-burst": "MCP call burst",
  "denial-burst": "Denial burst",
  "dlp-hit-burst": "DLP hit burst",
  "mcp-error-spike": "MCP error spike",
};

// The detector's own severity vocabulary, mapped onto the row-bar palette the
// rest of the Security Room uses. Anything unrecognised renders as "info" —
// never silently as "allow", which would paint an unknown finding green.
const SEVERITY_BAR: Record<string, Severity> = {
  critical: "crit",
  high: "deny",
  medium: "ask",
  low: "info",
};

// Severest first, then most-recently-updated. A burst that is still growing
// (updatedAt moves on every scan) outranks a stale one at the same severity.
const SEVERITY_ORDER: Record<string, number> = { critical: 0, high: 1, medium: 2, low: 3 };

function detectorLabel(detector: string): string {
  return DETECTOR_LABELS[detector] ?? detector;
}

function windowLabel(startMs: number, endMs: number): string {
  const start = new Date(startMs);
  const end = new Date(endMs);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return "";
  const fmt = (d: Date) =>
    d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false });
  return `${fmt(start)}–${fmt(end)}`;
}

export function AnomaliesPage() {
  const findings = useQuery((convexApi as any).anomaly.queries.activeForOrg, {}) as
    | Finding[]
    | undefined;
  // dismiss is admin-gated server-side (requireOrg role:"admin"). Read the same
  // role signal the exception drawer uses so a member sees why the control is
  // unavailable instead of collecting a 403 on click.
  const myRole = useQuery((convexApi as any).billing.queries.myRole, {}) as
    | { isAdmin?: boolean }
    | undefined;
  // Three states, not two. `myRole === undefined` means the role query has not
  // answered yet — and the findings query can resolve first, which would render
  // "admin only" AT an admin until the role landed. That tag is a claim about
  // the viewer's permissions; making it while we don't know is stating a
  // falsehood, so the unknown state shows no control rather than the wrong one.
  const roleKnown = myRole !== undefined;
  const isAdmin = myRole?.isAdmin === true;
  const dismissMut = useMutation((convexApi as any).anomaly.mutations.dismiss);

  const [confirming, setConfirming] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loading = findings === undefined;

  const sorted = useMemo(() => {
    if (!findings) return [];
    // activeForOrg maps every row, so it cannot emit a null or id-less entry.
    // The guard is here anyway because the sidebar badge guards this SAME
    // query, and a page that dies on input its own badge survives is
    // incoherent — one of the two would be wrong. An id-less entry is also
    // unactionable: dismiss takes the id, and `confirming === e.id` would match
    // every id-less row at once, arming them all on one click.
    return findings
      .filter((f): f is Finding => Boolean(f) && typeof f.id === "string")
      .sort((a, b) => {
        const sa = SEVERITY_ORDER[a.severity] ?? 99;
        const sb = SEVERITY_ORDER[b.severity] ?? 99;
        // A NaN comparator result is not "equal" — it makes the sort order
        // implementation-defined, so a non-numeric timestamp could scatter
        // severities rather than just misplacing one row.
        const ta = Number.isFinite(a.updatedAt) ? a.updatedAt : 0;
        const tb = Number.isFinite(b.updatedAt) ? b.updatedAt : 0;
        return sa - sb || tb - ta;
      });
  }, [findings]);

  const openCount = sorted.filter((f) => f.status === "open").length;

  async function dismiss(id: string) {
    setBusyId(id);
    setError(null);
    try {
      await dismissMut({ id });
      setConfirming(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not dismiss the finding.");
    } finally {
      setBusyId(null);
    }
  }

  return (
    <>
      <PageHeader
        title="Anomalies"
        subtitle={
          loading
            ? "scanning every 15 min"
            : `${sorted.length} active · ${openCount} open · scanned every 15 min`
        }
      />
      <div className="sr-page-body">
        {error && (
          <div className="sr-empty" role="alert">
            {error}
          </div>
        )}
        {loading && <div className="sr-empty">Loading anomalies…</div>}
        {!loading && (
          <QueueTable
            events={sorted.map((f) => ({
              id: f.id,
              // isoFrom, not `new Date(...).toISOString()`: the latter THROWS a
              // RangeError on an invalid value rather than rendering badly, and
              // a throw here unmounts the page whose entire job is to show that
              // something is wrong. The shared helper already guards every path.
              ts: isoFrom(f.updatedAt),
              severity: SEVERITY_BAR[f.severity] ?? ("info" as Severity),
              title: `${detectorLabel(f.detector)} — ${f.subject}`,
              // The numbers ARE the finding: "47 events against a threshold of
              // 10" is what an operator triages on, so it belongs on the row and
              // not behind a click.
              // Joined, not interpolated: windowLabel returns "" for an
              // unusable window, and a hard "· " would leave the row ending in
              // a separator pointing at nothing.
              who: [
                `${f.subjectKind} · ${f.count} vs threshold ${f.threshold}`,
                windowLabel(f.windowStart, f.windowEnd),
              ]
                .filter(Boolean)
                .join(" · "),
              rule: f.detector,
            }))}
            emptyMessage="No active anomalies. The scan runs every 15 minutes."
            rowAction={(e) => {
              if (!roleKnown) return null;
              if (!isAdmin) {
                return (
                  <span className="sr-field-tag" title="Only an org admin can dismiss a finding">
                    admin only
                  </span>
                );
              }
              if (confirming === e.id) {
                return (
                  <span className="sr-row-confirm">
                    <button
                      type="button"
                      className="sr-btn sr-btn--ghost"
                      onClick={() => setConfirming(null)}
                      disabled={busyId === e.id}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="sr-btn"
                      onClick={() => void dismiss(e.id)}
                      disabled={busyId === e.id}
                    >
                      {busyId === e.id ? "Dismissing…" : "Confirm dismiss"}
                    </button>
                  </span>
                );
              }
              return (
                <button
                  type="button"
                  className="sr-btn sr-btn--ghost"
                  // Two-step: a dismissed finding is never resurrected for the
                  // same subject (the dedup read binds status:"open"), so a
                  // stray click permanently hides a live security signal.
                  onClick={() => {
                    setError(null);
                    setConfirming(e.id);
                  }}
                >
                  Dismiss
                </button>
              );
            }}
          />
        )}
      </div>
    </>
  );
}
